import { authedFetch } from '@certara/common-react-components';

/**
 * API Error class
 * Extends Error with status code, status text, and optional user-friendly message
 */
export class ApiError extends Error {
  public userMessage?: string;

  constructor(
    message: string,
    public status: number,
    public statusText: string,
    userMessage?: string
  ) {
    super(message);
    this.name = 'ApiError';
    this.userMessage = userMessage;
  }
}
class ApiClient {
  private baseUrl: string;

  constructor() {
    // Prefer explicit env override; otherwise derive from current site base path
    // NOTE: window.baseUrl is a custom property, not a standard browser API.
    // It is set in the index.html script tag (or via server-side rendering) to indicate the base path for the app.
    const rawBase = typeof window.baseUrl === 'string' ? window.baseUrl : '/';
    const safeBasePath = this.getDerivedBasePath(rawBase);
    this.baseUrl = (
      import.meta.env.VITE_API_BASE_URL ||
      // When hosted under /Load, this becomes https://host/Load
      `${window.location.origin}${safeBasePath}`
    ).replace(/\/$/, '');
  }

  /**
   * Build full URL by prepending baseUrl to relative URLs
   */
  private buildUrl(url: string): string {
    // If URL is already absolute, return as-is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    // If we have a baseUrl, prepend it
    if (this.baseUrl) {
      // Remove leading slash from url if baseUrl already has trailing content
      const cleanUrl = url.startsWith('/') ? url : `/${url}`;
      return `${this.baseUrl}${cleanUrl}`;
    }

    // Otherwise return relative URL (for proxy)
    return url;
  }

  /**
   * Derive the base path, favoring the configured window.baseUrl,
   * and falling back to the first path segment from the current location.
   */
  private getDerivedBasePath(input: string): string {
    const safe = this.getSafeBasePath(input);
    if (safe && safe !== '/') {
      return safe;
    }
    // Fallback: infer from current pathname (e.g., /Load/Main -> /Load)
    const firstSegment = window.location.pathname.split('/').filter(Boolean)[0];
    if (firstSegment) {
      return `/${firstSegment}`;
    }
    return '/';
  }

  /**
   * Ensure base path is a safe, same-origin path segment.
   * - Resolves against current origin to strip protocol/host if present.
   * - Falls back to "/" on invalid input.
   * - Removes trailing slash for consistent concatenation.
   */
  private getSafeBasePath(input: string): string {
    try {
      const url = new URL(input, window.location.origin);
      const path = url.pathname || '/';
      return path.replace(/\/$/, '') || '/';
    } catch {
      return '/';
    }
  }

  /** Parse a JSON body, returning null for empty responses. */
  private async parseJson<T>(response: Response): Promise<T | null> {
    const text = await response.text();
    try {
      return text.length ? (JSON.parse(text) as T) : null;
    } catch {
      // JSON parse errors are handled by consuming code - don't show snackbar here
      throw new Error('Invalid JSON response from server');
    }
  }

  async getBlob(url: string, options?: { signal?: AbortSignal }): Promise<Blob> {
    // authedFetch attaches the bearer and handles the 401 → refresh → retry →
    // visible sign-in flow; we only map a surviving non-ok response to ApiError.
    const response = await authedFetch(this.buildUrl(url), {}, { signal: options?.signal });

    if (!response.ok) {
      throw new ApiError(
        `API Error: ${response.statusText}`,
        response.status,
        response.statusText
      );
    }

    return response.blob();
  }

  async get<T>(url: string, options?: { signal?: AbortSignal }): Promise<T | null> {
    const response = await authedFetch(this.buildUrl(url), {}, { signal: options?.signal });

    if (!response.ok) {
      throw new ApiError(
        `API Error: ${response.statusText}`,
        response.status,
        response.statusText
      );
    }

    return this.parseJson<T>(response);
  }

  async post<T>(url: string, data?: unknown, options?: { signal?: AbortSignal }): Promise<T | null> {
    const response = await authedFetch(
      this.buildUrl(url),
      {},
      {
        signal: options?.signal,
        // Rebuilt per attempt so the 401 retry resends a fresh body.
        makeInit: () => ({
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: data ? JSON.stringify(data) : undefined,
        }),
      }
    );

    if (!response.ok) {
      throw new ApiError(
        `API Error: ${response.statusText}`,
        response.status,
        response.statusText
      );
    }
    return this.parseJson<T>(response);
  }

  async delete(url: string, options?: { signal?: AbortSignal }): Promise<void> {
    const response = await authedFetch(
      this.buildUrl(url),
      {},
      { signal: options?.signal, makeInit: () => ({ method: 'DELETE' }) }
    );

    if (!response.ok) {
      throw new ApiError(
        `API Error: ${response.statusText}`,
        response.status,
        response.statusText
      );
    }
  }

  /**
   * Convert object to form-urlencoded string (jQuery-style serialization)
   * Handles nested objects and arrays like jQuery's $.param()
   */
  private objectToFormData(obj: Record<string, unknown>, prefix = ''): string {
    const parts: string[] = [];

    for (const [key, value] of Object.entries(obj)) {
      const fullKey = prefix ? `${prefix}[${key}]` : key;

      if (value === null || value === undefined) {
        continue;
      }

      if (Array.isArray(value)) {
        value.forEach((item, index) => {
          if (typeof item === 'object' && item !== null) {
            parts.push(this.objectToFormData(item as Record<string, unknown>, `${fullKey}[${index}]`));
          } else {
            parts.push(`${encodeURIComponent(`${fullKey}[${index}]`)}=${encodeURIComponent(String(item))}`);
          }
        });
      } else if (typeof value === 'object') {
        parts.push(this.objectToFormData(value as Record<string, unknown>, fullKey));
      } else {
        parts.push(`${encodeURIComponent(fullKey)}=${encodeURIComponent(String(value))}`);
      }
    }

    return parts.filter(Boolean).join('&');
  }

  /**
   * POST with form-urlencoded data (for MVC controllers that expect form data)
   */
  async postForm<T>(url: string, data?: Record<string, unknown>, options?: { signal?: AbortSignal }): Promise<T | null> {
    const response = await authedFetch(
      this.buildUrl(url),
      {},
      {
        signal: options?.signal,
        makeInit: () => ({
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: data ? this.objectToFormData(data) : undefined,
        }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new ApiError(
        `API Error: ${response.statusText}`,
        response.status,
        errorText || response.statusText
      );
    }

    return this.parseJson<T>(response);
  }
}

export const apiClient = new ApiClient();
